import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import {
  type AppointmentStatus,
  appointmentStatusSchema,
  clinicIdSchema,
  getClinicTimezone,
  omitNullish,
  toWallClockDate,
} from '@snootsvet/shared'

import { calendarViewTypes } from '@/frontend/features/appointments/appointment-calendar.helpers'
import type { AppointmentCalendarState } from '@/frontend/features/appointments/appointment-calendar-provider'
import { useDefaultClinic } from '@/frontend/hooks/use-default-clinic'

const DEFAULT_STATUSES = appointmentStatusSchema.options.filter((status) => status !== 'cancelled')
const VALID_STATUSES = new Set<AppointmentStatus>(appointmentStatusSchema.options)

const filterSchema = z.object({
  clinic: clinicIdSchema.optional().catch(undefined),
  staff: z
    .string()
    .transform((s) => s.split(',').filter((id) => z.uuid().safeParse(id).success))
    .catch([]),
  status: z
    .string()
    .nullable()
    .transform((s) => {
      const statuses = (s ?? '')
        .split(',')
        .filter((v): v is AppointmentStatus => VALID_STATUSES.has(v as AppointmentStatus))
      return statuses.length ? statuses : DEFAULT_STATUSES
    })
    .catch(DEFAULT_STATUSES),
  view: z.enum(calendarViewTypes).catch('week'),
  date: z.iso.date().optional().catch(undefined),
  time: z.iso.time({ precision: -1 }).optional().catch(undefined),
})

const FILTERS_STORAGE_KEY = 'appointments-filters'
const PERSISTED_FILTER_KEYS: string[] = [
  'clinic',
  'staff',
  'status',
  'view',
] as const satisfies (keyof z.infer<typeof filterSchema>)[]

function loadSavedFilters(): Record<string, string> {
  try {
    const stringified = localStorage.getItem(FILTERS_STORAGE_KEY) ?? '{}'
    const parsed = JSON.parse(stringified)
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, string] =>
          PERSISTED_FILTER_KEYS.includes(entry[0]) && typeof entry[1] === 'string'
      )
    )
  } catch {
    return {}
  }
}

/** An empty URL inherits saved filters; otherwise absent staff and status values select defaults. */
export function resolveFilterInput(searchParams: URLSearchParams, saved: Record<string, string>) {
  const isInitialMount = searchParams.size === 0
  return {
    clinic: searchParams.get('clinic') ?? saved.clinic ?? undefined,
    staff: searchParams.has('staff')
      ? searchParams.get('staff')
      : isInitialMount
        ? (saved.staff ?? '')
        : '',
    status: searchParams.has('status')
      ? searchParams.get('status')
      : isInitialMount
        ? (saved.status ?? null)
        : null,
    view: searchParams.get('view') ?? saved.view ?? 'week',
    date: searchParams.get('date') ?? undefined,
    time: searchParams.get('time') ?? undefined,
  }
}

/**
 * Persists calendar position and filters in the URL, with clinic, view, staff and status also saved locally.
 * An empty URL inherits saved filters; a populated URL uses defaults for omitted staff and status filters.
 * Missing or empty status values select all statuses except cancelled. Selecting every status, including cancelled, uses an explicit list.
 * Missing or empty staff values select all scheduled vets. Default selections are omitted from the URL and saved filters; status order is irrelevant.
 * Date remains URL-only. Time is captured once for initial scrolling and then removed from the URL.
 */
export function usePersistedCalendarState() {
  const defaultClinic = useDefaultClinic()
  const [searchParams, setSearchParams] = useSearchParams()
  const paramsRef = useRef(searchParams)
  paramsRef.current = searchParams
  const parsed = filterSchema.parse(resolveFilterInput(searchParams, loadSavedFilters()))
  const [initialTime] = useState(parsed.time)
  const clinicId = parsed.clinic ?? defaultClinic.clinic?.id
  const date =
    parsed.date ?? (clinicId ? toWallClockDate(new Date(), getClinicTimezone(clinicId)) : undefined)
  const view = parsed.view

  const updateParams = useCallback(
    (updates: Record<string, string | null> = {}) => {
      const current = paramsRef.current
      const input = {
        ...resolveFilterInput(current, loadSavedFilters()),
        ...Object.fromEntries(current),
        ...updates,
      }
      const statuses = new Set(filterSchema.shape.status.parse(input.status))
      const isDefaultStatus =
        statuses.size === DEFAULT_STATUSES.length &&
        DEFAULT_STATUSES.every((status) => statuses.has(status))
      const next = omitNullish({
        ...input,
        status: isDefaultStatus ? null : [...statuses].join(','),
        staff: filterSchema.shape.staff.parse(input.staff).join(',') || null,
      })
      localStorage.setItem(
        FILTERS_STORAGE_KEY,
        JSON.stringify(
          Object.fromEntries(
            Object.entries(next).filter(([key]) => PERSISTED_FILTER_KEYS.includes(key))
          )
        )
      )
      paramsRef.current = new URLSearchParams(next)
      if (paramsRef.current.toString() !== current.toString()) {
        setSearchParams(paramsRef.current, { replace: true })
      }
    },
    [setSearchParams]
  )

  useEffect(() => {
    if (clinicId && date) updateParams({ clinic: clinicId, view, date, time: null })
  }, [clinicId, view, date, updateParams])

  const onChange = useCallback(
    (changes: Partial<AppointmentCalendarState>) => {
      const { clinicId, staffIds, statuses, ...position } = changes
      updateParams({
        ...position,
        ...(clinicId ? { clinic: clinicId } : {}),
        ...(staffIds ? { staff: staffIds.length > 0 ? staffIds.join(',') : null } : {}),
        ...(statuses ? { status: statuses.join(',') } : {}),
      })
    },
    [updateParams]
  )

  return {
    ...(defaultClinic.isLoading || !clinicId || !date
      ? { isLoading: true as const, value: null }
      : {
          isLoading: false as const,
          value: {
            clinicId,
            date,
            view,
            staffIds: parsed.staff,
            statuses: parsed.status,
          } satisfies AppointmentCalendarState,
        }),
    initialTime,
    onChange,
    resetFilters: () => updateParams({ staff: null, status: null }),
  }
}
